import React, { useState, useEffect } from 'react';

const Hotspot = ({ top, left, title, description, active, onClick }) => {
  return (
    <div
      className="absolute group z-10"
      style={{ top: `${top}%`, left: `${left}%` }}
    >
      <button
        onClick={onClick}
        className={`w-8 h-8 rounded-full flex items-center justify-center transition-all duration-300 shadow-lg border-2 border-white
          ${active ? 'bg-purple-600 scale-125' : 'bg-primary hover:bg-purple-600 hover:scale-110'} 
          animate-bounce-subtle`}
      >
        <span className="text-white font-bold text-lg leading-none">+</span>
      </button>

      {!active && (
        <span className="absolute inset-0 rounded-full bg-primary/40 animate-ping pointer-events-none"></span>
      )}
    </div>
  );
};

const InteractiveGuide = ({ t }) => {
  const [activeSection, setActiveSection] = useState('overview');
  const [activeHotspot, setActiveHotspot] = useState(null);
  const [isImageLoading, setIsImageLoading] = useState(true);

  // Trigger loading effect whenever the section changes
  useEffect(() => {
    setIsImageLoading(true);
    setActiveHotspot(null);
  }, [activeSection]);

  const sections = [
    { id: 'overview', title: t.guide_overview_title || "1. Dashboard Overview", icon: '🏠' },
    { id: 'ordering', title: t.guide_ordering_postpaid_title || "2. Ordering (Postpaid)", icon: '🛒' },
    { id: 'ordering_prepaid', title: t.guide_ordering_prepaid_title || "3. Ordering (Prepaid)", icon: '💳' },
    { id: 'webshop', title: t.guide_webshop_title || "4. Product Management", icon: '⚙️' },
    { id: 'renewals', title: t.guide_renewals_title || "5. Tracking Renewals", icon: '📅' },
    { id: 'afas', title: t.guide_afas_title || "6. AFAS Integration", icon: '📄' },
    { id: 'customers', title: t.guide_customers_title || "7. Customers Management", icon: '👥' },
  ];

  const guideData = {
    overview: {
      title: t.guide_overview_title || 'Dashboard Overview',
      subtitle: t.guide_overview_subtitle || 'Identify the key areas of the customer dashboard.',
      image: '/ui/home.png',
      hotspots: [
        { id: 1, top: 12, left: 15, title: t.hs_customer_selector_title, description: t.hs_customer_selector_desc },
        { id: 2, top: 23, left: 55, title: t.hs_postpaid_toggle_title, description: t.hs_postpaid_toggle_desc },
        { id: 3, top: 33, left: 40, title: t.hs_support_hours_title, description: t.hs_support_hours_desc },
      ]
    },
    ordering: {
      title: t.guide_ordering_postpaid_title || 'Ordering & Renewing (Postpaid)',
      subtitle: t.guide_ordering_postpaid_subtitle || 'Learn how to increase licenses or extend subscriptions for postpaid customers.',
      image: '/ui/home-2.png',
      hotspots: [
        { id: 4, top: 45, left: 18, title: t.hs_existing_subs_title, description: t.hs_existing_subs_desc },
        { id: 5, top: 55, left: 60, title: t.hs_renew_btn_title, description: t.hs_renew_btn_desc },
        { id: 6, top: 45, left: 53, title: t.hs_increase_lic_title, description: t.hs_increase_lic_desc },
        { id: 7, top: 70, left: 80, title: t.hs_place_order_title, description: t.hs_place_order_desc },
      ]
    },
    ordering_prepaid: {
      title: t.guide_ordering_prepaid_title || 'Ordering & Renewing (Prepaid)',
      subtitle: t.guide_ordering_prepaid_subtitle || 'For regular customers, payment via Stripe or PayPal is required.',
      image: '/ui/home-3.png',
      hotspots: [
        { id: 4, top: 45, left: 18, title: t.hs_existing_subs_title, description: t.hs_existing_subs_desc },
        { id: 5, top: 55, left: 60, title: t.hs_renew_btn_title, description: t.hs_renew_btn_desc },
        { id: 6, top: 45, left: 53, title: t.hs_increase_lic_title, description: t.hs_increase_lic_desc },
        { id: 7, top: 85, left: 80, title: t.hs_place_order_title, description: t.hs_place_order_desc },
        { id: 8, top: 60, left: 80, title: t.hs_payment_methods_title, description: t.hs_payment_methods_desc },
      ]
    },
    webshop: {
      title: t.guide_webshop_title || 'Product & Price Management',
      subtitle: t.guide_webshop_subtitle || 'How to keep your webshop catalog up to date.',
      image: '/ui/webshop.png',
      hotspots: [
        { id: 7, top: 40, left: 55, title: t.hs_price_updates_title, description: t.hs_price_updates_desc },
        { id: 8, top: 40, left: 65, title: t.hs_tech_sku_title, description: t.hs_tech_sku_desc },
        { id: 9, top: 15, left: 90, title: t.hs_add_item_title, description: t.hs_add_item_desc },
      ]
    },
    renewals: {
      title: t.guide_renewals_title || 'Renewal Tracking',
      subtitle: t.guide_renewals_subtitle || 'Proactively manage upcoming expirations.',
      image: '/ui/renewals-summary.png',
      hotspots: [
        { id: 10, top: 30, left: 10, title: t.hs_weekly_sum_title, description: t.hs_weekly_sum_desc },
        { id: 11, top: 75, left: 50, title: t.hs_drilldown_title, description: t.hs_drilldown_desc },
      ]
    },
    afas: {
      title: t.guide_afas_title || 'AFAS Integration',
      subtitle: t.guide_afas_subtitle || 'Review synchronized data and PDF invoices.',
      image: '/ui/afas-transactions.png',
      hotspots: [
        { id: 12, top: 20, left: 20, title: t.hs_sync_status_title, description: t.hs_sync_status_desc },
        { id: 13, top: 10, left: 90, title: t.hs_invoice_pdf_title, description: t.hs_invoice_pdf_desc },
      ]
    },
    customers: {
      title: t.guide_customers_title || 'Customer Management',
      subtitle: t.guide_customers_subtitle || 'Maintain customer records and billing settings.',
      image: '/ui/customers.png',
      hotspots: [
        { id: 14, top: 20, left: 10, title: t.hs_customer_list_title, description: t.hs_customer_list_desc },
        { id: 15, top: 25, left: 60, title: t.hs_postpaid_toggle_title, description: t.hs_postpaid_toggle_desc },
        { id: 16, top: 35, left: 40, title: t.hs_debtor_nr_title, description: t.hs_debtor_nr_desc },
      ]
    }
  };

  const currentData = guideData[activeSection];

  return (
    <div className="flex flex-col lg:flex-row min-h-[600px] overflow-hidden">
      {/* Sidebar */}
      <div className="w-full lg:w-[320px] lg:min-w-[320px] lg:max-w-[320px] flex-shrink-0 border-r border-gray-200 flex flex-col">
        <nav className="flex-grow p-4 space-y-2">
          {sections.map(section => (
            <button
              key={section.id}
              onClick={() => {
                setActiveSection(section.id);
                setActiveHotspot(null);
              }}
              className={`w-full text-left p-3 rounded-xl transition-all duration-200 flex items-center gap-3 font-bold text-sm
                ${activeSection === section.id
                  ? 'bg-purple-600 text-white shadow-md transform translate-x-2'
                  : 'text-gray-500 hover:bg-gray-200'}`}
            >
              <span className="text-lg">{section.icon}</span>
              {section.title}
            </button>
          ))}
        </nav>
      </div>

      {/* Main Content */}
      <div className="flex-grow p-8 flex flex-col">
        <div className="mb-8">
          <div className="flex flex-col md:flex-row md:items-center justify-between gap-4 mb-2">
            <h1 className="text-3xl font-black text-gray-900">{currentData.title}</h1>
            <div className="inline-flex items-center gap-2 px-3 py-1 bg-purple-50 text-purple-700 rounded-full text-xs font-bold shrink-0">
              <span className="w-2 h-2 bg-purple-600 rounded-full animate-pulse"></span>
              {t.guide_instruction ? t.guide_instruction.split('{icon}').map((part, index) => (
                <React.Fragment key={index}>
                  {part}
                  {index === 0 && <span className="w-4 h-4 bg-purple-600 text-white rounded-full flex items-center justify-center text-[10px]">+</span>}
                </React.Fragment>
              )) : "Click each highlighted area marked + before you click Next."}
            </div>
          </div>
          <p className="text-gray-500 font-medium">{currentData.subtitle}</p>
        </div>

        <div className="relative flex-grow bg-white rounded-2xl border border-gray-200 overflow-hidden shadow-sm min-h-[400px]">
          <div className="relative w-full h-full min-h-[400px]">
            {isImageLoading && (
              <div className="absolute inset-0 bg-gray-50 flex flex-col items-center justify-center animate-pulse z-0 overflow-hidden">
                <div className="absolute inset-0 bg-gradient-to-r from-transparent via-white/40 to-transparent -translate-x-full animate-shimmer"></div>
                <div className="flex flex-col items-center gap-3 text-purple-300">
                  <svg className="w-12 h-12 text-purple-400/70" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="1.5" d="M4 16l4.586-4.586a2 2 0 012.828 0L16 16m-2-2l1.586-1.586a2 2 0 012.828 0L20 14m-6-6h.01M6 20h12a2 2 0 002-2V6a2 2 0 00-2-2H6a2 2 0 00-2 2v12a2 2 0 002 2z" />
                  </svg>
                  <span className="text-xs font-bold tracking-widest uppercase text-purple-400 animate-pulse">Laden...</span>
                </div>
              </div>
            )}
            <img
              src={currentData.image}
              alt={currentData.title}
              onLoad={() => setIsImageLoading(false)}
              onError={() => setIsImageLoading(false)}
              className={`w-full h-full object-cover transition-all duration-500 transform ${activeHotspot ? 'brightness-[0.4] blur-[2px]' : ''} ${isImageLoading ? 'opacity-0 scale-95 blur-md' : 'opacity-100 scale-100 blur-none'}`}
            />

            {!activeHotspot && currentData.hotspots.map(spot => (
              <Hotspot
                key={spot.id}
                {...spot}
                active={activeHotspot === spot.id}
                onClick={() => setActiveHotspot(spot)}
              />
            ))}

            {activeHotspot && (
              <div className="absolute inset-0 flex items-center justify-center p-8 z-20 animate-fade-in">
                <div className="bg-white rounded-3xl shadow-2xl p-8 max-w-md w-full relative border border-gray-100 transform transition-all">
                  <button
                    onClick={() => setActiveHotspot(null)}
                    className="absolute top-4 right-4 text-gray-400 hover:text-gray-600 p-2"
                  >
                    <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M6 18L18 6M6 6l12 12" /></svg>
                  </button>

                  <div className="mb-4">
                    <span className="inline-block px-3 py-1 bg-purple-100 text-purple-700 rounded-lg text-xs font-black uppercase mb-2">
                      {t.step_detail || "Step Detail"}
                    </span>
                    <h3 className="text-2xl font-black text-gray-900 mb-3">{activeHotspot.title}</h3>
                    <p className="text-gray-600 leading-relaxed font-medium">
                      {activeHotspot.description}
                    </p>
                  </div>

                  <button
                    onClick={() => setActiveHotspot(null)}
                    className="w-full bg-purple-600 hover:bg-purple-700 text-white font-bold py-3 rounded-xl transition-colors shadow-lg"
                  >
                    {t.got_it || "Got it!"}
                  </button>
                </div>
              </div>
            )}
          </div>
        </div>

        {/* Footer Navigation */}
        <div className="mt-8 flex justify-between items-center border-t border-gray-100 pt-6">
          <button
            disabled={activeSection === sections[0].id}
            onClick={() => setActiveSection(sections[sections.findIndex(s => s.id === activeSection) - 1].id)}
            className="px-6 py-2 text-gray-500 font-bold hover:text-purple-600 disabled:opacity-30 flex items-center gap-2"
          >
            ← {t.previous_step || "Previous"}
          </button>

          <div className="flex gap-2">
            {sections.map(s => (
              <div key={s.id} className={`w-2 h-2 rounded-full transition-all duration-300 ${activeSection === s.id ? 'bg-purple-600 w-8' : 'bg-gray-200'}`}></div>
            ))}
          </div>

          <button
            disabled={activeSection === sections[sections.length - 1].id}
            onClick={() => setActiveSection(sections[sections.findIndex(s => s.id === activeSection) + 1].id)}
            className="px-8 py-2 bg-gray-900 hover:bg-purple-600 text-white rounded-xl font-bold transition-all disabled:opacity-30"
          >
            {t.next_step || "Next"} →
          </button>
        </div>
      </div>

      <style>{`
        @keyframes bounce-subtle {
          0%, 100% { transform: translateY(0); }
          50% { transform: translateY(-5px); }
        }
        .animate-bounce-subtle {
          animation: bounce-subtle 2s infinite ease-in-out;
        }
        @keyframes fade-in {
          from { opacity: 0; transform: scale(0.95); }
          to { opacity: 1; transform: scale(1); }
        }
        .animate-fade-in {
          animation: fade-in 0.3s ease-out forwards;
        }
        @keyframes shimmer {
          100% { transform: translateX(100%); }
        }
        .animate-shimmer {
          animation: shimmer 1.5s infinite;
        }
      `}</style>
    </div>
  );
};

export default InteractiveGuide;
